
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  batchLinkContactsToCompanies, 
  findCompanyInconsistencies, 
  findOrCreateCompany, 
  linkContactToCompany,
  fixCompanyInconsistency,
  autoLinkContactToCompany
} from "@/utils/companyLinkUtils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, AlertCircle, RefreshCw, Check, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const CompanyLinkingTool = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stats, setStats] = useState<{ total: number; linked: number; errors: number } | null>(null);
  const [inconsistencies, setInconsistencies] = useState<any[]>([]);
  const [loadingInconsistencies, setLoadingInconsistencies] = useState(false);
  const [summary, setSummary] = useState<{ total: number; withCompany: number; withEmployer: number; needsLinking: number } | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [selectedAction, setSelectedAction] = useState<'update_company' | 'update_employer'>('update_company');

  const handleProgress = (processed: number, total: number) => {
    const percentage = Math.round((processed / total) * 100);
    setProgress(percentage);
  };

  const runLinkProcess = async () => {
    if (isRunning) return;
    
    setIsRunning(true);
    setProgress(0);
    setStats(null);
    
    try {
      const results = await batchLinkContactsToCompanies(50, handleProgress);
      setStats(results);
      
      if (results.linked > 0) {
        toast.success(`Linked ${results.linked} contacts to companies`);
      }
      
      if (results.errors > 0) {
        toast.error(`Failed to link ${results.errors} contacts`);
      }
      
      await loadSummary();
    } catch (error) {
      console.error("Error running link process:", error);
      toast.error("An error occurred while linking contacts to companies");
    } finally {
      setIsRunning(false);
    }
  };

  const findInconsistentLinks = async () => {
    setLoadingInconsistencies(true);
    setInconsistencies([]);
    
    try {
      const results = await findCompanyInconsistencies();
      setInconsistencies(results);
      
      if (results.length === 0) {
        toast.success("No inconsistencies found");
      } else {
        toast.info(`Found ${results.length} contacts with potential inconsistencies`);
      }
    } catch (error) {
      console.error("Error finding inconsistencies:", error);
      toast.error("An error occurred while checking for inconsistencies");
    } finally {
      setLoadingInconsistencies(false);
    }
  };

  const handleFixInconsistency = async (contactId: number) => {
    const success = await fixCompanyInconsistency(contactId, selectedAction);
    
    if (success) {
      // Remove fixed item from the list
      setInconsistencies(prev => prev.filter(item => item.id !== contactId));
      toast.success(`Fixed inconsistency for contact ID ${contactId}`);
      // Refresh summary after fix
      loadSummary();
    }
  };

  const manuallyLinkContact = async (contactId: number, employerName: string) => {
    try {
      const companyId = await findOrCreateCompany(employerName);
      
      if (companyId) {
        const success = await linkContactToCompany(contactId, companyId);
        
        if (success) {
          toast.success(`Updated company for contact ${contactId}`);
          // Remove from the list
          setInconsistencies(prev => prev.filter(item => item.id !== contactId));
          return true;
        } else {
          toast.error(`Failed to update company for contact ${contactId}`);
        }
      } else {
        toast.error(`Failed to find or create company ${employerName}`);
      }
    } catch (error) {
      console.error("Error fixing inconsistency:", error);
      toast.error("An error occurred while fixing the inconsistency");
    }
    
    return false;
  };

  const loadSummary = async () => {
    setLoadingSummary(true);
    
    try {
      // Count total contacts
      const { count: totalCount, error: totalError } = await supabase
        .from('connections_new')
        .select('*', { count: 'exact', head: true });
        
      if (totalError) throw totalError;
      
      // Count contacts with company_id
      const { count: withCompanyCount, error: companyError } = await supabase
        .from('connections_new')
        .select('*', { count: 'exact', head: true })
        .not('company_id', 'is', null);
        
      if (companyError) throw companyError;
      
      // Count contacts with current_employer
      const { count: withEmployerCount, error: employerError } = await supabase
        .from('connections_new')
        .select('*', { count: 'exact', head: true })
        .not('current_employer', 'is', null);
        
      if (employerError) throw employerError;
      
      // Count contacts that need linking
      const { count: needsLinkingCount, error: needsLinkingError } = await supabase
        .from('connections_new')
        .select('*', { count: 'exact', head: true })
        .not('current_employer', 'is', null)
        .is('company_id', null);
        
      if (needsLinkingError) throw needsLinkingError;
      
      setSummary({
        total: totalCount || 0,
        withCompany: withCompanyCount || 0,
        withEmployer: withEmployerCount || 0,
        needsLinking: needsLinkingCount || 0
      });
    } catch (error) {
      console.error("Error loading summary:", error);
      toast.error("Failed to load contact summary");
    } finally {
      setLoadingSummary(false);
    }
  };

  const testSingleAutoLink = async () => {
    try {
      // Get a random contact with current_employer but no company_id
      const { data: contact, error: contactError } = await supabase
        .from('connections_new')
        .select('id, current_employer')
        .not('current_employer', 'is', null)
        .is('company_id', null)
        .limit(1)
        .single();
        
      if (contactError || !contact) {
        toast.error("No contacts found that need linking or error occurred");
        return;
      }
      
      toast.info(`Testing auto-link for contact ${contact.id} with employer "${contact.current_employer}"`);
      
      const success = await autoLinkContactToCompany(contact.id);
      
      if (success) {
        toast.success(`Successfully auto-linked contact ${contact.id} to company`);
        loadSummary(); // Refresh summary
      } else {
        toast.error(`Failed to auto-link contact ${contact.id}`);
      }
    } catch (error) {
      console.error("Error testing auto-link:", error);
      toast.error("An error occurred while testing auto-link");
    }
  };

  React.useEffect(() => {
    loadSummary();
  }, []);

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle>Company Linking Tool</CardTitle>
        <CardDescription>
          Link contacts to companies based on their current employer information
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="summary">
          <TabsList className="mb-4">
            <TabsTrigger value="summary">Summary</TabsTrigger>
            <TabsTrigger value="linking">Link Contacts</TabsTrigger>
            <TabsTrigger value="inconsistencies">Find Inconsistencies</TabsTrigger>
          </TabsList>
          
          <TabsContent value="summary">
            {loadingSummary ? (
              <div className="flex justify-center items-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : summary ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardHeader className="py-4 px-6">
                      <CardTitle className="text-lg">Total Contacts</CardTitle>
                    </CardHeader>
                    <CardContent className="py-2 px-6">
                      <p className="text-3xl font-bold">{summary.total}</p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="py-4 px-6">
                      <CardTitle className="text-lg">With Company ID</CardTitle>
                    </CardHeader>
                    <CardContent className="py-2 px-6">
                      <p className="text-3xl font-bold">{summary.withCompany}</p>
                      <p className="text-sm text-muted-foreground">
                        {Math.round((summary.withCompany / summary.total) * 100)}%
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="py-4 px-6">
                      <CardTitle className="text-lg">With Employer</CardTitle>
                    </CardHeader>
                    <CardContent className="py-2 px-6">
                      <p className="text-3xl font-bold">{summary.withEmployer}</p>
                      <p className="text-sm text-muted-foreground">
                        {Math.round((summary.withEmployer / summary.total) * 100)}%
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="py-4 px-6">
                      <CardTitle className="text-lg">Needs Linking</CardTitle>
                    </CardHeader>
                    <CardContent className="py-2 px-6">
                      <p className="text-3xl font-bold">{summary.needsLinking}</p>
                      <p className="text-sm text-muted-foreground">
                        {Math.round((summary.needsLinking / summary.total) * 100)}%
                      </p>
                    </CardContent>
                  </Card>
                </div>
                
                <div className="flex justify-end gap-2">
                  <Button onClick={testSingleAutoLink} variant="outline" size="sm">
                    Test Auto-Link
                  </Button>
                  <Button onClick={loadSummary} variant="outline" size="sm">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                Failed to load summary
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="linking">
            <div className="space-y-4">
              {stats && (
                <div className="bg-muted p-4 rounded-md">
                  <p className="font-medium">Results:</p>
                  <ul className="mt-2 space-y-1">
                    <li>Total contacts processed: {stats.total}</li>
                    <li>Successfully linked: {stats.linked}</li>
                    <li>Errors: {stats.errors}</li>
                  </ul>
                </div>
              )}
              
              {isRunning && (
                <div className="space-y-2">
                  <Progress value={progress} className="h-2" />
                  <p className="text-xs text-center text-muted-foreground">
                    {progress}% Complete
                  </p>
                </div>
              )}
              
              <div className="flex justify-center">
                <Button 
                  onClick={runLinkProcess} 
                  disabled={isRunning}
                  className="w-full md:w-auto"
                >
                  {isRunning ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Linking Contacts...
                    </>
                  ) : (
                    'Link Contacts to Companies'
                  )}
                </Button>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="inconsistencies">
            <div className="space-y-4">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2 md:gap-4">
                <p className="text-sm text-muted-foreground">
                  This tool finds contacts where the current employer doesn't match the linked company name
                </p>
                
                <div className="flex gap-2">
                  <Select 
                    value={selectedAction}
                    onValueChange={(value) => setSelectedAction(value as 'update_company' | 'update_employer')}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Fix action" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="update_company">Update Company Link</SelectItem>
                      <SelectItem value="update_employer">Update Employer Name</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Button 
                    onClick={findInconsistentLinks} 
                    disabled={loadingInconsistencies}
                    size="sm"
                    variant="outline"
                  >
                    {loadingInconsistencies ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Checking...
                      </>
                    ) : (
                      'Find Inconsistencies'
                    )}
                  </Button>
                </div>
              </div>
              
              {loadingInconsistencies ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : inconsistencies.length > 0 ? (
                <div className="border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Contact</TableHead>
                        <TableHead>Current Employer</TableHead>
                        <TableHead>Linked Company</TableHead>
                        <TableHead>Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {inconsistencies.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>{item.name}</TableCell>
                          <TableCell>{item.current_employer}</TableCell>
                          <TableCell>{item.company?.company_name}</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 px-2"
                                onClick={() => handleFixInconsistency(item.id)}
                                title={selectedAction === 'update_company' ? 
                                  'Update company link based on employer name' : 
                                  'Update employer name based on linked company'}
                              >
                                <Check className="h-4 w-4 text-green-500" />
                                <span className="sr-only">Fix</span>
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  {inconsistencies.length === 0 && !loadingInconsistencies ? (
                    "No inconsistencies found or check not yet run"
                  ) : (
                    "No inconsistencies found"
                  )}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
      <CardFooter className="border-t pt-6">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center">
            <AlertCircle className="h-4 w-4 text-amber-500 mr-2" />
            <p className="text-xs text-muted-foreground">
              This tool manages the relationship between contacts and companies. Make sure to run it periodically to maintain data consistency.
            </p>
          </div>
        </div>
      </CardFooter>
    </Card>
  );
};
